import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetch } from '@/lib/api';
import GratitudeBanner from '@/components/notifications/GratitudeBanner';

interface NotificationContextType {
  showGratitudeBanner: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function useNotificationContext() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
}

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [gratitudeMessage, setGratitudeMessage] = useState<string>('');
  const [showGratitude, setShowGratitude] = useState<boolean>(false);
  
  // Track the IDs of notifications we've already shown
  const [processedNotificationIds, setProcessedNotificationIds] = useState<Set<number>>(new Set());
  
  // Poll for new notifications every 15 seconds
  useEffect(() => {
    const checkForNewNotifications = async () => {
      try {
        const response = await fetch('/api/notifications?type=gratitude&unread=true', { credentials: 'include' });
        if (response.ok) {
          const text = await response.text();
          let data: any[] = [];
          try { data = JSON.parse(text); } catch { data = []; }
          if (data && data.length > 0) {
            // Get the most recent notification
            const latestNotification = data[0];
            
            // Only show the notification if we haven't processed this ID yet
            if (latestNotification.id && !processedNotificationIds.has(latestNotification.id)) {
              // Add this notification ID to our processed set
              setProcessedNotificationIds(prev => {
                const newSet = new Set(prev);
                newSet.add(latestNotification.id);
                return newSet;
              });
              
              setGratitudeMessage(latestNotification.message || 'Someone appreciated your presence');
              setShowGratitude(true);
              
              // Mark as read
              await fetch(`/api/notifications/${latestNotification.id}/read`, { method: 'POST' });
            }
          }
        }
      } catch (error) {
        console.error('Error checking for notifications:', error);
      }
    };
    
    // For demo purposes, we'll check infrequently to avoid excessive API calls
    const interval = setInterval(checkForNewNotifications, 30000);
    
    // Check once on component mount with a delay to avoid immediate notification
    const initialCheck = setTimeout(checkForNewNotifications, 1000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(initialCheck);
    };
  }, [processedNotificationIds]);
  
  // Function to manually show the gratitude banner
  const showGratitudeBanner = (message: string) => {
    setGratitudeMessage(message);
    setShowGratitude(true);
  };
  
  // Close the banner
  const closeGratitudeBanner = () => {
    setShowGratitude(false);
  };
  
  return (
    <NotificationContext.Provider value={{ showGratitudeBanner }}>
      {children}
      <GratitudeBanner 
        message={gratitudeMessage}
        isVisible={showGratitude}
        onClose={closeGratitudeBanner}
      />
    </NotificationContext.Provider>
  );
}
